import { ArrowForward, Info } from '@mui/icons-material';
import { Box, Button, Divider, Typography } from '@mui/material';
import { CollaborativeChartsReviewItem, GenericDateValue } from '@uplift/api';
import { useCollaborativeChartsReviewItemResourceSubmissionDetails, useUpdateCollaborativeChartsReviewItemMutation } from '@uplift/queries';
import { FormikAutoSave, RadioControl, ScrollableView, Tooltip } from '@uplift/ui-v2';
import { Formik } from 'formik';
import React from 'react';
import { ChartsReviewFlagSection } from './Flagging';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';


const QuestionnaireQuestion: React.VFC<{
  title: React.ReactNode;
  subTitle?: React.ReactNode;
  name: string;
  disabled?: boolean;
}> = (props) => {
  return (
    <Box>
      <Typography variant="body1" fontWeight={500}>{props.title}</Typography>
      {props.subTitle && <Typography sx={{ mt: '2px', mb: '4px' }} variant="body2">{props.subTitle}</Typography>}
      <RadioControl
        name={props.name}
        disabled={props.disabled}
        values={[
          { value: 'yes', label: 'Yes' },
          { value: 'partially', label: 'Partially' },
          { value: 'no', label: 'No' },
          { value: 'not_applicable', label: 'Not applicable' },
        ]}
      />
    </Box>
  );
};


const DateOrDateTimeValueText: React.VFC<{ value: GenericDateValue }> = (props) => {
  const timezone = useTimezone();

  if (props.value.type === 'date') {
    const dateValue = CalendarDateService.date(props.value.value, DateFormats.ISO_DATE);

    return <>{dateValue.format('MMM D')}</>;
  }
  const datetimeValue = CalendarDateService.date(props.value.value).tz(timezone);

  return <>{datetimeValue.format('MMM D, YYYY [at] hh:mm A')}</>;
};

const NoteSubmittedWithin72HrsBlock: React.VFC<{ chartsReviewItem: CollaborativeChartsReviewItem }> = ({ chartsReviewItem }) => {
  const { data: submissionDetails } = useCollaborativeChartsReviewItemResourceSubmissionDetails(chartsReviewItem.reviewId, chartsReviewItem.resourceId);

  return (
    <Box>
      <Typography variant="body1" fontWeight={500}>
        Was the note submitted within 72 hours of <Box component="span" sx={{ whiteSpace: 'nowrap' }}>visit completion?
          <Tooltip
            placement="bottom"
            title={(
              <Typography variant="body2" color="inherit">
                We fill this in for you based on when the note was submitted.<br />
                <strong>Visit ended</strong>: <DateOrDateTimeValueText value={submissionDetails.serviceCompletedAt} /><br />
                <strong>Note submitted</strong>: <DateOrDateTimeValueText value={submissionDetails.submittedAt} />
              </Typography>
            )}
          >
            <Info color="info" fontSize="small" sx={{ marginBottom: '-5px', marginLeft: '3px' }} />
          </Tooltip>
        </Box>
      </Typography>
      <Typography variant="body2" fontWeight={500}>Submitted and signed on <DateOrDateTimeValueText value={submissionDetails.submittedAt} /></Typography>
      <RadioControl
        name="noteSubmittedWithin72Hrs"
        disabled
        values={[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]}
      />
    </Box>
  );
};

export const Questionnaire: React.VFC<{
  chartsReviewItem: CollaborativeChartsReviewItem;
  flagged: boolean;
  setFlagged: (flagged: boolean) => void;
  disabled?: boolean;
  onGoNext?: () => void;
}> = ({ chartsReviewItem, disabled, flagged, setFlagged, onGoNext }) => {
  const mutation = useUpdateCollaborativeChartsReviewItemMutation(chartsReviewItem.reviewId, chartsReviewItem.resourceId);

  return (
    <Formik
      enableReinitialize
      initialValues={chartsReviewItem}
      onSubmit={async (values: CollaborativeChartsReviewItem) => {
        mutation.mutateAsync(values);
      }}
    >
      {formik => (
        <>
          <ScrollableView sx={{ bgcolor: 'grey.50' }} showScrollShadow>
            {flagged && (
              <>
                <Box sx={{ bgcolor: 'grey.50', p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Typography variant="h4">Flag for Clinical Review</Typography>
                  <ChartsReviewFlagSection chartsReviewItem={chartsReviewItem} setFlagged={setFlagged} />
                </Box>
                <Divider />
              </>
            )}
            <Box sx={{ flex: 1, display: 'flex', bgcolor: 'common.white' }}>
              <FormikAutoSave delayMs={100} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
                <Typography variant="h4" sx={{ mt: 2 }}>Review Questions</Typography>
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="hpiClinicallyRelevant"
                  title="Is the HPI clinically relevant to the encounter, capturing patient narrative and concerns?"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="intervalUpdatesDocumented"
                  title="Are appropriate interval updates assessed and documented?"
                  subTitle="e.g. relevant symptoms, severity, treatment response, side effects, etc."
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="diagnosesSupportedByEvidence"
                  title="Are diagnoses supported by the patient history and objective findings?"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="medicationIndicationsDocumented"
                  title="Are medication indications documented, with clinical rationale provided when indicated?"
                  subTitle="e.g., unusual off-label use or unusual polypharmacy approach"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="mdmConsistentWithGuidelines"
                  title="Is medical decision making consistent with evidence-based guidelines and standards of care?"
                  subTitle="e.g., medications, dosing, medical considerations, diagnostic and monitoring steps, referrals, etc., when applicable"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="nonPharmInterventionsDocumented"
                  title="Are non-pharmacologic interventions considered and documented when appropriate?"
                  subTitle="e.g., education, therapy, diet, lifestyle, safety planning, etc."
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="followupPlanDocumented"
                  title="Is there a documented plan for follow up?"
                  subTitle="e.g., documented date or weeks"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="cptCodeAccurate"
                  title="Is the CPT code accurate and supported by the documented time and/or risk and complexity?"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="documentationSupportsContinuity"
                  title="Is documentation structure clear, up to date, consistent and supportive of continuity of care by another clinician?"
                  subTitle="e.g., no contradictions"
                />
                <Divider />

                <NoteSubmittedWithin72HrsBlock chartsReviewItem={chartsReviewItem} />
              </Box>

            </Box>
          </ScrollableView>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              endIcon={<ArrowForward />}
              onClick={onGoNext}
              loading={formik.dirty || formik.isSubmitting}
              loadingPosition="end"
            >
              Next Chart
            </Button>
          </Box>
        </>
      )}
    </Formik>
  );
};
